import cx from 'classnames'
import React, { Children, isValidElement } from 'react'
import { useSelected } from '@commutifi-fe/hooks'
import { getArrayValue } from '@commutifi-fe/utils'
import CheckboxButtonCard from './CheckboxButtonCard'
import RadioButtonCard from './RadioButtonCard'
import { JustifyValues } from './buttonCardProps'
import './styles.scss'

const prefixCls = 'selectable-card'

export { JustifyValues }
export enum SizeTypes {
  Default = 'default',
  Small = 'small'
}
export { RadioButtonCard, CheckboxButtonCard }

type SelectedCardType = string | string[]

interface SelectableCardGridContextInterface {
  onSelect: (e: React.MouseEvent | React.KeyboardEvent, id: string | null) => void
  selected: Set<string>
  multiSelect: boolean
}

// Thanks to Kent C. Dodds for the inspiration:
// https://kentcdodds.com/blog/compound-components-with-react-hooks
const SelectableCardGridContext = React.createContext<SelectableCardGridContextInterface | null>(null)

interface SelectableCardGridProps {
  // Antd form item id
  id?: string
  // block: option to fit buttons width to its parent width
  block?: boolean
  multiSelect?: boolean
  className?: string
  onChange?: (selected: SelectedCardType | null) => void
  equalWidth?: boolean
  cardSize?: `${SizeTypes}`
  initialSelectedCards?: SelectedCardType | null
  selectedCards?: SelectedCardType | null
  // When equalWidth is set to false, the bases width of the items
  // will be set automatically with the content. If you want to set
  // a width that items should have unless they can grow to fill the row
  // you need to use this prop
  baseWidth?: number | 'auto'
  children: React.ReactNode
}

interface ClickableCardProps {
  id: string
  title?: React.ReactNode
  description?: React.ReactNode
  justify?: `${JustifyValues}`
  cover?: React.ReactNode
  disabled?: boolean
  contentStyle?: Record<string, unknown>
  isSelected?: boolean
  initialSelected?: boolean
  onClick?: (e: React.MouseEvent | React.KeyboardEvent, id: string) => void
  className?: string
  children?: React.ReactNode
}

function SelectableCardGrid({
  id,
  multiSelect = false,
  children,
  className,
  onChange,
  equalWidth = false,
  initialSelectedCards,
  selectedCards,
  cardSize = 'default',
  block = false,
  baseWidth
}: SelectableCardGridProps) {
  const { selected, toggle } = useSelected({
    initialSelected: initialSelectedCards ? getArrayValue(initialSelectedCards) : undefined,
    selected: selectedCards,
    multiSelect
  })

  const handleOnSelect = React.useCallback(
    (e: React.MouseEvent | React.KeyboardEvent, id: string | null) => {
      if (!id) {
        return
      }
      const newSelected = toggle(id)
      if (onChange) {
        onChange(multiSelect ? [...newSelected] : [...newSelected].length ? [...newSelected][0] : null)
      }
    },
    [multiSelect, onChange, toggle]
  )

  const commonCardClasses = cx(
    'app-selectable-card-grid-item-wrapper',
    `${prefixCls}-item-wrapper`,
    cardSize === 'small' && `${prefixCls}-item-wrapper--small`,
    block && `${prefixCls}-item-wrapper--block`
  )

  const value = React.useMemo(
    () => ({ onSelect: handleOnSelect, selected, multiSelect }),
    [handleOnSelect, multiSelect, selected]
  )

  return (
    <SelectableCardGridContext.Provider value={value}>
      <div
        id={id}
        className={cx(`${prefixCls}-group`, equalWidth && `${prefixCls}-group--equal`, className)}
        data-testid="selectable-card-grid"
      >
        {Children.map(children, (child, index) => {
          if (!child || !isValidElement(child)) {
            return null
          }

          return (
            <div
              key={index}
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- Let's trust the user
              id={child.props.id || `selectable-card-${index}`}
              className={commonCardClasses}
              style={{
                ...(baseWidth && { flexBasis: typeof baseWidth === 'number' ? `${baseWidth}px` : baseWidth })
              }}
            >
              {child}
            </div>
          )
        })}
        {/* We need to add invisible components so flexbox works with same width items */}
        {equalWidth && Children.count(children) > 2
          ? Children.map(children, (child, index) => (
              <div key={index} className={cx(commonCardClasses, `${prefixCls}-item--invisible`)} />
            ))
          : null}
      </div>
    </SelectableCardGridContext.Provider>
  )
}

const useSelectableCardGridContext = () => {
  const context = React.useContext(SelectableCardGridContext)
  if (!context) {
    throw new Error('ClickableCard compound components cannot be rendered outside the SelectableCardGrid component')
  }
  return context
}

const ClickableCard = React.forwardRef<HTMLDivElement, ClickableCardProps>(
  ({ id, onClick, className, children, ...rest }, ref) => {
    const { onSelect, selected, multiSelect } = useSelectableCardGridContext()
    const handleOnClick = (e: React.MouseEvent | React.KeyboardEvent) => {
      onSelect(e, id)
      if (onClick) {
        onClick(e, id)
      }
    }

    // Use checkbox card component with different styling if multiselect is true
    const DisplayComponent = multiSelect ? CheckboxButtonCard : RadioButtonCard
    return (
      <DisplayComponent ref={ref} onClick={handleOnClick} isSelected={selected.has(id)} className={className} {...rest}>
        {children}
      </DisplayComponent>
    )
  }
)
ClickableCard.displayName = 'ClickableCard'

SelectableCardGrid.ClickableCard = ClickableCard
export default SelectableCardGrid
